import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Home,
  Refresh,
  MoreVert,
  CheckCircle,
  Cancel,
  Pending,
  AccessTime,
  Close,
  Edit,
  Visibility,
  Phone,
  Email,
  LocationOn,
  ArrowUpward,
  ArrowDownward,
  Person,
  NavigateNext,
  NavigateBefore,
  PlayArrow,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { productService } from '../../services/productService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

const VisitRequestsPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [visitRequests, setVisitRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [productDetails, setProductDetails] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Status configuration
  const statusConfig = {
    PENDING: { 
      label: "Pending Inspection", 
      color: "#FFA726", 
      bgColor: "#FFF3E0", 
      icon: <AccessTime sx={{ color: "#FFA726" }} /> 
    },
    APPROVED: { 
      label: "Approved", 
      color: "#66BB6A", 
      bgColor: "#E8F5E9", 
      icon: <CheckCircle sx={{ color: "#66BB6A" }} /> 
    },
    REJECTED: { 
      label: "Rejected", 
      color: "#EF5350", 
      bgColor: "#FFEBEE", 
      icon: <Cancel sx={{ color: "#EF5350" }} /> 
    },
    COMPLETED: { 
      label: "Completed", 
      color: "#4CAF50", 
      bgColor: "#E8F5E9", 
      icon: <CheckCircle sx={{ color: "#4CAF50" }} /> 
    },
    CANCELLED: { 
      label: "Cancelled", 
      color: "#EF5350", 
      bgColor: "#FFEBEE", 
      icon: <Close sx={{ color: "#EF5350" }} /> 
    },
    VISIT_DELAYED: { 
      label: "Visit Delayed", 
      color: "#FF7043", 
      bgColor: "#FFF3E0", 
      icon: <Pending sx={{ color: "#FF7043" }} /> 
    }
  };

  // Fetch all visit requests
  const fetchVisitRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/visit-requests`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setVisitRequests(data);
    } catch (err) {
      console.error('Error fetching visit requests:', err);
      setError('Failed to fetch visit requests');
      toast.error('Failed to fetch visit requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'ADMIN') {
      fetchVisitRequests();
    }
  }, [user]);

  // Calculate status counts
  const getStatusCounts = () => {
    const counts = {
      PENDING: 0,
      APPROVED: 0,
      COMPLETED: 0,
      CANCELLED: 0,
      VISIT_DELAYED: 0
    };

    visitRequests.forEach(request => {
      if (counts.hasOwnProperty(request.status)) {
        counts[request.status]++;
      }
    });

    return counts;
  };

  // Filter requests by status
  const getFilteredRequests = () => {
    if (statusFilter === 'all') {
      return visitRequests;
    }
    return visitRequests.filter(request => request.status === statusFilter);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sorted requests
  const getSortedRequests = (requests) => {
    if (!sortField) return requests;

    return [...requests].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'customer':
          aValue = a.customerName || `${a.customer?.firstName} ${a.customer?.lastName}` || '';
          bValue = b.customerName || `${b.customer?.firstName} ${b.customer?.lastName}` || '';
          break;
        case 'product':
          aValue = a.productName || a.product?.name || '';
          bValue = b.productName || b.product?.name || '';
          break;
        case 'seller':
          aValue = a.seller?.businessProfile?.businessName || `${a.seller?.firstName} ${a.seller?.lastName}` || '';
          bValue = b.seller?.businessProfile?.businessName || `${b.seller?.firstName} ${b.seller?.lastName}` || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'requestDate':
          aValue = new Date(a.requestDate || a.createdAt);
          bValue = new Date(b.requestDate || b.createdAt);
          break;
        case 'location':
          aValue = a.location || '';
          bValue = b.location || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle view request
  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setViewDialogOpen(true);
    setCurrentImageIndex(0);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRequest(null);
    setProductDetails(null);
    setCurrentImageIndex(0);
  };

  // Fetch product details when selectedRequest changes
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (selectedRequest && selectedRequest.productId) {
        setProductLoading(true);
        try {
          const product = await productService.getProductById(selectedRequest.productId);
          setProductDetails(product.product || product);
        } catch (error) {
          console.error('Error fetching product details:', error);
          setProductDetails(null);
        } finally {
          setProductLoading(false);
        }
      } else {
        setProductDetails(null);
      }
    };

    fetchProductDetails();
  }, [selectedRequest]);

  // Get all product images and videos (primary first, then others)
  const getProductMedia = () => {
    try {
      if (!productDetails) return { images: [], videos: [], allMedia: [] };
      
      const images = [];
      const videos = [];
      
      // Add primary image first if available
      if (productDetails.primaryImageUrl) {
        images.push(productDetails.primaryImageUrl);
      }
      
      // Add images from imageUrls array (if exists)
      if (productDetails.imageUrls && Array.isArray(productDetails.imageUrls)) {
        productDetails.imageUrls.forEach(mediaUrl => {
          if (mediaUrl) {
            const isVideo = mediaUrl.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
            if (isVideo && mediaUrl !== productDetails.primaryImageUrl) {
              videos.push(mediaUrl);
            } else if (!isVideo && mediaUrl !== productDetails.primaryImageUrl) {
              images.push(mediaUrl);
            }
          }
        });
      }
      
      // Add other images/videos from images array
      if (productDetails.images && Array.isArray(productDetails.images)) {
        productDetails.images.forEach(img => {
          const mediaUrl = typeof img === 'string' ? img : (img.url || img.imageUrl || img.videoUrl);
          if (mediaUrl) {
            const isVideo = mediaUrl.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/) || img.type === 'video';
            if (isVideo && mediaUrl !== productDetails.primaryImageUrl && !videos.includes(mediaUrl)) {
              videos.push(mediaUrl);
            } else if (!isVideo && mediaUrl !== productDetails.primaryImageUrl && !images.includes(mediaUrl)) {
              images.push(mediaUrl);
            }
          }
        });
      }
      
      // Combine images and videos for display
      const allMedia = [...images, ...videos];
      
      return { images, videos, allMedia };
    } catch (error) {
      console.error('Error getting product media:', error);
      return { images: [], videos: [], allMedia: [] };
    }
  };

  const mediaResult = getProductMedia();
  const productImages = mediaResult?.images || [];
  const productVideos = mediaResult?.videos || [];
  const productMedia = mediaResult?.allMedia || [];

  const handleNextMedia = () => {
    if (productMedia && productMedia.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % productMedia.length);
    }
  };

  const handlePreviousMedia = () => {
    if (productMedia && productMedia.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + productMedia.length) % productMedia.length);
    }
  };

  const isCurrentMediaVideo = () => {
    if (!productMedia || productMedia.length === 0 || !productMedia[currentImageIndex]) return false;
    const currentMedia = productMedia[currentImageIndex];
    return currentMedia && typeof currentMedia === 'string' && currentMedia.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
  };

  // Handle status update
  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/visit-requests/${requestId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        // Update the local state
        setVisitRequests(prevRequests =>
          prevRequests.map(request =>
            request.id === requestId ? { ...request, status: newStatus } : request
          )
        );
        toast.success('Visit request status updated successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to update status');
        console.error('Failed to update status:', errorData);
      }
    } catch (error) {
      toast.error('Error updating status. Please try again.');
      console.error('Error updating status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if user is admin
  if (!user || user.role !== 'ADMIN') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You do not have permission to access this page.</Typography>
        </Alert>
      </Container>
    );
  }

  const statusCounts = getStatusCounts();
  const filteredRequests = getFilteredRequests();
  const sortedRequests = getSortedRequests(filteredRequests);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/" color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Typography color="text.primary">Visit Requests</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Visit Request Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track customer visit requests for your properties and products
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchVisitRequests}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <AccessTime />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Requests
                  </Typography>
                  <Typography variant="h4">
                    {loading ? '...' : visitRequests.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <Pending />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Pending Inspection
                  </Typography>
                  <Typography variant="h4">
                    {loading ? '...' : statusCounts.PENDING || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                  <Cancel />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Cancelled
                  </Typography>
                  <Typography variant="h4">
                    {loading ? '...' : statusCounts.CANCELLED || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Completed
                  </Typography>
                  <Typography variant="h4">
                    {loading ? '...' : statusCounts.COMPLETED || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Controls */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status Filter"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            {Object.entries(statusConfig).map(([status, config]) => (
              <MenuItem key={status} value={status}>
                {config.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {filteredRequests.length} requests
        </Typography>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Visit Requests Table */}
      {!loading && !error && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Visit Requests
              {statusFilter !== 'all' && (
                <Chip 
                  label={statusConfig[statusFilter].label} 
                  size="small" 
                  sx={{ ml: 2, bgcolor: statusConfig[statusFilter].bgColor, color: statusConfig[statusFilter].color }}
                />
              )}
            </Typography>
            
            {filteredRequests.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {statusFilter === 'all' ? 'No visit requests found' : `No ${statusConfig[statusFilter].label.toLowerCase()} requests`}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('customer')}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Customer
                          {sortField === 'customer' && (
                            sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('product')}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Product
                          {sortField === 'product' && (
                            sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('seller')}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Seller
                          {sortField === 'seller' && (
                            sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('status')}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Status
                          {sortField === 'status' && (
                            sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('requestDate')}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Request Date
                          {sortField === 'requestDate' && (
                            sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('location')}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Location
                          {sortField === 'location' && (
                            sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRequests.map((request) => (
                      <TableRow key={request.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">
                              {request.customerName || `${request.customer?.firstName} ${request.customer?.lastName}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {request.customerEmail || request.customer?.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {request.productName || request.product?.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {request.seller?.businessProfile?.businessName || 
                             `${request.seller?.firstName} ${request.seller?.lastName}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusConfig[request.status]?.label || request.status}
                            size="small"
                            sx={{
                              bgcolor: statusConfig[request.status]?.bgColor || '#f5f5f5',
                              color: statusConfig[request.status]?.color || '#666'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(request.requestDate || request.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {request.location || 'Not specified'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             <FormControl size="small" sx={{ minWidth: 120 }}>
                               <Select
                                 value={request.status}
                                 onChange={(e) => handleStatusUpdate(request.id, e.target.value)}
                                 displayEmpty
                                 disabled={loading}
                               >
                                 <MenuItem value="PENDING">Pending Inspection</MenuItem>
                                 <MenuItem value="APPROVED">Approved</MenuItem>
                                 <MenuItem value="REJECTED">Rejected</MenuItem>
                                 <MenuItem value="COMPLETED">Completed</MenuItem>
                                 <MenuItem value="CANCELLED">Cancelled</MenuItem>
                               </Select>
                             </FormControl>
                             <IconButton
                               size="small"
                               onClick={() => handleViewRequest(request)}
                               title="View Details"
                             >
                               <Visibility />
                             </IconButton>
                           </Box>
                         </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}



      {/* View Request Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility sx={{ color: 'primary.main' }} />
            Visit Request Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ pt: 1 }}>
              {/* Customer Information */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                    <Person fontSize="small" />
                  </Avatar>
                  Customer Information
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedRequest.customerName || `${selectedRequest.customer?.firstName} ${selectedRequest.customer?.lastName}`}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedRequest.customerEmail || selectedRequest.customer?.email}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedRequest.customerPhone || selectedRequest.customer?.phoneNumber || 'Not provided'}
                  </Typography>
                </Box>
              </Paper>

              {/* Request Information */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                    <Visibility fontSize="small" />
                  </Avatar>
                  Request Information
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Product</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedRequest.productName || selectedRequest.product?.name}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={statusConfig[selectedRequest.status]?.label || selectedRequest.status}
                      size="medium"
                      sx={{
                        bgcolor: statusConfig[selectedRequest.status]?.bgColor || '#f5f5f5',
                        color: statusConfig[selectedRequest.status]?.color || '#666',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Request Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatDate(selectedRequest.requestDate || selectedRequest.createdAt)}
                  </Typography>
                </Box>
                {selectedRequest.preferredDate && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Preferred Visit Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {new Date(selectedRequest.preferredDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      {selectedRequest.preferredTime && (
                        <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                          at {selectedRequest.preferredTime}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Product Images/Videos */}
              {selectedRequest.productId && (
                <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                      <Visibility fontSize="small" />
                    </Avatar>
                    Product Images & Videos
                  </Typography>
                  {productLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary">Loading product media...</Typography>
                    </Box>
                    ) : productMedia && productMedia.length > 0 ? (
                    <Box>
                      <Box sx={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: 500 }}>
                        {isCurrentMediaVideo() ? (
                          <Box
                            component="video"
                            src={productMedia && productMedia[currentImageIndex]?.startsWith('http') 
                              ? productMedia[currentImageIndex] 
                              : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8083'}${productMedia?.[currentImageIndex] || ''}`}
                            controls
                            sx={{
                              width: '100%',
                              maxWidth: 500,
                              height: 'auto',
                              maxHeight: 400,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              display: 'block',
                              bgcolor: '#000'
                            }}
                            onError={(e) => {
                              console.error('Error loading video:', e);
                            }}
                          />
                        ) : (
                          <Box
                            component="img"
                            src={productMedia && productMedia[currentImageIndex]?.startsWith('http') 
                              ? productMedia[currentImageIndex] 
                              : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8083'}${productMedia?.[currentImageIndex] || ''}`}
                            alt={`Product ${isCurrentMediaVideo() ? 'video' : 'image'} ${currentImageIndex + 1}`}
                            sx={{
                              width: '100%',
                              maxWidth: 500,
                              height: 'auto',
                              maxHeight: 400,
                              objectFit: 'contain',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              display: 'block',
                              bgcolor: '#f5f5f5'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        {productMedia && productMedia.length > 1 && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center', alignItems: 'center' }}>
                            <IconButton
                              onClick={handlePreviousMedia}
                              size="small"
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' }
                              }}
                            >
                              <NavigateBefore />
                            </IconButton>
                            <Typography variant="body2" sx={{ px: 2, fontWeight: 600 }}>
                              {currentImageIndex + 1} / {productMedia.length}
                            </Typography>
                            <IconButton
                              onClick={handleNextMedia}
                              size="small"
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' }
                              }}
                            >
                              <NavigateNext />
                            </IconButton>
                          </Box>
                        )}
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                          {productImages && productImages.length > 0 && (
                              <Chip 
                                label={`${productImages.length} Image${productImages.length !== 1 ? 's' : ''}`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            )}
                            {productVideos && productVideos.length > 0 && (
                            <Chip 
                              label={`${productVideos.length} Video${productVideos.length !== 1 ? 's' : ''}`} 
                              size="small" 
                              color="secondary" 
                              variant="outlined"
                              icon={<PlayArrow />}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                      No product images or videos available
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Additional Details */}
              <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                    <LocationOn fontSize="small" />
                  </Avatar>
                  Additional Details
                </Typography>
                <Box sx={{ mb: selectedRequest.notes ? 1 : 0 }}>
                  <Typography variant="body2" color="text.secondary">Location</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedRequest.location || 'Not specified'}
                  </Typography>
                </Box>
                {selectedRequest.notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.7 }}>
                      {selectedRequest.notes}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VisitRequestsPage;